import {
  fetchAgenticEquityQuotes,
  fetchAgenticOptionQuotes,
  fetchAgenticSnapshot,
  fetchAgenticStatus,
  type AgenticRobinhoodAccount,
  type AgenticSnapshot,
  type AgenticSnapshotAccountData,
} from "../api/agenticClient";
import { parseRhEquityQuoteMap } from "../shared/rhEquityQuote";
import {
  computeRhOptionPnl,
  parseRhOptionQuoteMap,
  resolveRhOptionMid,
  rhOptionPopForSide,
  type RhOptionQuote,
} from "../shared/rhOptionQuote";
import { buildOccSymbol, normalizeRhOptionPremium } from "./wheelActions";
import { normalizeTicker, type OptionPosition, type PositionsSnapshot } from "./watchlistData";

export type PositionsFeedSource = "stockforge" | "robinhood";

const AGENTIC_SELECTED_ACCOUNT_KEY = "sfe-agentic-selected-account";

type RhOptionRow = {
  ticker: string;
  side: "call" | "put";
  strike: number;
  expDate: string;
  quantity: number;
  avgPrice: number | null;
  optionSymbol: string;
  optionId: string | null;
  chainId: string | null;
  isShort: boolean;
  raw: Record<string, unknown>;
};

function readNumber(obj: Record<string, unknown>, ...keys: string[]): number | null {
  for (const key of keys) {
    const value = obj[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string") {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}

function readSnapshotData(
  entry: AgenticSnapshotAccountData | undefined,
  key: keyof AgenticSnapshotAccountData,
): unknown {
  const block = entry?.[key];
  if (!block || typeof block !== "object" || !("ok" in block) || !block.ok) return null;
  const data = block.data;
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  return (data as { data?: unknown }).data ?? data;
}

function parseRhOptionSide(item: Record<string, unknown>): "call" | "put" | null {
  for (const key of ["option_type", "instrument_type"]) {
    const raw = item[key];
    if (typeof raw !== "string") continue;
    const lower = raw.toLowerCase();
    if (lower.includes("call")) return "call";
    if (lower.includes("put")) return "put";
  }
  for (const key of ["side", "type"]) {
    const raw = item[key];
    if (typeof raw !== "string") continue;
    const lower = raw.toLowerCase();
    if (lower.includes("call")) return "call";
    if (lower.includes("put")) return "put";
  }
  return null;
}

function parseRhOptionRow(row: unknown): RhOptionRow | null {
  if (!row || typeof row !== "object" || Array.isArray(row)) return null;
  const item = row as Record<string, unknown>;
  const tickerRaw =
    (typeof item.chain_symbol === "string" && item.chain_symbol) ||
    (typeof item.symbol === "string" && item.symbol) ||
    (typeof item.underlying_symbol === "string" && item.underlying_symbol) ||
    "";
  const ticker = normalizeTicker(tickerRaw);
  const side = parseRhOptionSide(item);
  const strike = readNumber(item, "strike_price", "strike");
  const expDateRaw = item.expiration_date ?? item.exp_date;
  const expDate = typeof expDateRaw === "string" ? expDateRaw.slice(0, 10) : "";
  const quantity = readNumber(item, "quantity");
  if (!ticker || !side || strike == null || !expDate || quantity == null || !(Math.abs(quantity) > 0)) {
    return null;
  }
  const avgPriceRaw = readNumber(
    item,
    "average_price",
    "avg_price",
    "premium",
    "average_open_price",
    "intraday_average_open_price",
  );
  const multiplier = readNumber(item, "trade_value_multiplier");
  const avgPrice =
    avgPriceRaw != null ? normalizeRhOptionPremium(avgPriceRaw, multiplier ?? 100) : null;
  const optionSymbol =
    (typeof item.option_symbol === "string" && item.option_symbol) ||
    buildOccSymbol(ticker, expDate, side, strike);
  const optionId = typeof item.option_id === "string" ? item.option_id.trim() : null;
  const chainId = typeof item.chain_id === "string" ? item.chain_id.trim() : null;
  const typeRaw = typeof item.type === "string" ? item.type.toLowerCase() : "";
  const isShort = typeRaw.includes("short") || (!typeRaw.includes("long") && quantity < 0);
  return {
    ticker,
    side,
    strike,
    expDate,
    quantity: Math.abs(quantity),
    avgPrice,
    optionSymbol,
    optionId,
    chainId,
    isShort,
    raw: item,
  };
}

function optionContractKey(ticker: string, side: string, strike: number, expDate: string): string {
  const sideNorm = side.toLowerCase().includes("call") ? "call" : "put";
  return `${normalizeTicker(ticker)}|${sideNorm}|${strike}|${expDate.slice(0, 10)}`;
}

function resolveAccountNumber(
  snapshot: AgenticSnapshot,
  preferred: string | null,
): string | null {
  const accounts = snapshot.accounts ?? [];
  if (!accounts.length) return null;
  if (preferred && accounts.some((account) => account.accountNumber === preferred)) {
    return preferred;
  }
  try {
    const stored = localStorage.getItem(AGENTIC_SELECTED_ACCOUNT_KEY)?.trim();
    if (stored && accounts.some((account) => account.accountNumber === stored)) {
      return stored;
    }
  } catch {
    /* ignore */
  }
  return accounts.find((account: AgenticRobinhoodAccount) => account.isDefault)?.accountNumber ?? accounts[0].accountNumber;
}

function collectRhOptionRows(
  snapshot: AgenticSnapshot,
  accountNumber: string | null,
): RhOptionRow[] {
  const byKey = new Map<string, RhOptionRow>();
  if (!snapshot.byAccount) return [];

  const entries: Array<[string, AgenticSnapshotAccountData | undefined]> = accountNumber
    ? [[accountNumber, snapshot.byAccount[accountNumber]]]
    : Object.entries(snapshot.byAccount);

  for (const [, entry] of entries) {
    const positionsRaw = readSnapshotData(entry, "optionPositions");
    if (!positionsRaw || typeof positionsRaw !== "object" || Array.isArray(positionsRaw)) continue;
    const positions = (positionsRaw as { positions?: unknown }).positions;
    if (!Array.isArray(positions)) continue;
    for (const row of positions) {
      const parsed = parseRhOptionRow(row);
      if (!parsed) continue;
      const key = optionContractKey(parsed.ticker, parsed.side, parsed.strike, parsed.expDate);
      const existing = byKey.get(key);
      if (!existing) {
        byKey.set(key, parsed);
        continue;
      }
      existing.quantity += parsed.quantity;
      if (existing.avgPrice == null && parsed.avgPrice != null) {
        existing.avgPrice = parsed.avgPrice;
      }
      if (!existing.optionId && parsed.optionId) {
        existing.optionId = parsed.optionId;
      }
      if (!existing.chainId && parsed.chainId) {
        existing.chainId = parsed.chainId;
      }
    }
  }

  return [...byKey.values()];
}

function collectRhOptionQuoteMap(
  snapshot: AgenticSnapshot,
  accountNumber: string | null,
): Map<string, RhOptionQuote> {
  const map = new Map<string, RhOptionQuote>();
  if (!snapshot.byAccount) return map;

  const entries: Array<[string, AgenticSnapshotAccountData | undefined]> = accountNumber
    ? [[accountNumber, snapshot.byAccount[accountNumber]]]
    : Object.entries(snapshot.byAccount);

  for (const [, entry] of entries) {
    const quotesRaw = readSnapshotData(entry, "optionQuotes");
    if (!quotesRaw || typeof quotesRaw !== "object" || Array.isArray(quotesRaw)) continue;
    const results = (quotesRaw as { results?: unknown }).results;
    for (const [instrumentId, quote] of parseRhOptionQuoteMap(results)) {
      if (!map.has(instrumentId)) map.set(instrumentId, quote);
    }
  }

  return map;
}

function rhRowToOptionPosition(
  row: RhOptionRow,
  dte: number | null,
  equityQuotes: Map<string, number>,
  optionQuotes: Map<string, RhOptionQuote>,
): OptionPosition {
  const quote = row.optionId ? optionQuotes.get(row.optionId) : undefined;
  const raw =
    row.raw && typeof row.raw === "object" && !Array.isArray(row.raw)
      ? (row.raw as Record<string, unknown>)
      : {};
  const mid = quote?.mid ?? resolveRhOptionMid(raw) ?? undefined;
  const stockPrice =
    readNumber(
      row.raw,
      "underlying_price",
      "chain_underlying_price",
      "underlying_asset_price",
    ) ?? equityQuotes.get(row.ticker) ?? undefined;
  const currentProfit = readNumber(
    row.raw,
    "unrealized_gain_loss",
    "unrealized_pl",
    "unrealized_gain_loss_amount",
  );
  const currentProfitPct = readNumber(
    row.raw,
    "unrealized_gain_loss_percentage",
    "unrealized_pl_pct",
  );
  const entry = row.avgPrice;
  const openCredit = entry != null ? entry * 100 * row.quantity : undefined;
  const computedPnl =
    mid != null && entry != null
      ? computeRhOptionPnl(entry, mid, row.quantity, row.isShort)
      : { profit: null, profitPct: null };
  const pop = quote ? rhOptionPopForSide(quote, row.isShort) : null;

  return {
    option_symbol: row.optionSymbol,
    option_id: row.optionId ?? undefined,
    chain_id: row.chainId ?? undefined,
    ticker: row.ticker,
    strike: row.strike,
    side: row.side,
    exp_date: row.expDate,
    contracts: row.quantity,
    entry_price: entry ?? undefined,
    open_credit: openCredit,
    current_price: mid,
    stock_price: stockPrice ?? undefined,
    current_profit: computedPnl.profit ?? currentProfit ?? undefined,
    current_profit_pct: computedPnl.profitPct ?? currentProfitPct ?? undefined,
    pop: pop ?? undefined,
    dte: dte ?? undefined,
    expired: dte != null ? dte < 0 : false,
  };
}

function collectRhEquityQuotePrices(
  snapshot: AgenticSnapshot,
  accountNumber: string | null,
): Map<string, number> {
  const map = new Map<string, number>();
  if (!snapshot.byAccount) return map;

  const entries: Array<[string, AgenticSnapshotAccountData | undefined]> = accountNumber
    ? [[accountNumber, snapshot.byAccount[accountNumber]]]
    : Object.entries(snapshot.byAccount);

  for (const [, entry] of entries) {
    const quotesRaw = readSnapshotData(entry, "equityQuotes");
    if (!quotesRaw || typeof quotesRaw !== "object" || Array.isArray(quotesRaw)) continue;
    const results = (quotesRaw as { results?: unknown }).results;
    for (const [symbol, price] of parseRhEquityQuoteMap(results)) {
      if (!map.has(symbol)) map.set(symbol, price);
    }
  }

  return map;
}

function mergeSfOptionMetadata(
  rhOptions: OptionPosition[],
  sfSnapshot: PositionsSnapshot | null,
): OptionPosition[] {
  if (!sfSnapshot?.options.length) return rhOptions;
  const byKey = new Map<string, OptionPosition>();
  for (const pos of sfSnapshot.options) {
    byKey.set(optionContractKey(pos.ticker, pos.side, pos.strike, pos.exp_date), pos);
  }
  return rhOptions.map((rh) => {
    const sf = byKey.get(optionContractKey(rh.ticker, rh.side, rh.strike, rh.exp_date));
    if (!sf) return rh;
    return {
      ...rh,
      sector: rh.sector ?? sf.sector,
      target_price: rh.target_price ?? sf.target_price,
      open_date: rh.open_date ?? sf.open_date,
      pop: rh.pop ?? sf.pop,
      collateral: rh.collateral ?? sf.collateral,
    };
  });
}

export async function rhSnapshotToOptionPositions(
  snapshot: AgenticSnapshot,
  accountNumber: string | null,
  sfSnapshot: PositionsSnapshot | null,
  remainingDteFn: (exp: string) => number | null,
): Promise<OptionPosition[]> {
  const rows = collectRhOptionRows(snapshot, accountNumber);
  let equityQuotes = collectRhEquityQuotePrices(snapshot, accountNumber);
  let optionQuotes = collectRhOptionQuoteMap(snapshot, accountNumber);
  const tickers = [...new Set(rows.map((row) => row.ticker))];
  if (tickers.length) {
    try {
      const live = await fetchAgenticEquityQuotes(tickers);
      equityQuotes = new Map([...equityQuotes, ...live]);
    } catch {
      /* keep snapshot quotes only */
    }
  }
  const optionIds = [...new Set(rows.map((row) => row.optionId).filter(Boolean) as string[])];
  if (optionIds.length) {
    try {
      const live = await fetchAgenticOptionQuotes(optionIds);
      optionQuotes = new Map([...optionQuotes, ...live]);
    } catch {
      /* keep snapshot quotes only */
    }
  }
  const options = rows.map((row) =>
    rhRowToOptionPosition(row, remainingDteFn(row.expDate), equityQuotes, optionQuotes),
  );
  return mergeSfOptionMetadata(options, sfSnapshot);
}

export async function tryRobinhoodPositionsSnapshot(
  sfSnapshot: PositionsSnapshot | null,
  remainingDteFn: (exp: string) => number | null,
): Promise<PositionsSnapshot | null> {
  try {
    const status = await fetchAgenticStatus();
    if (!status.connected) return null;
    const snapRes = await fetchAgenticSnapshot();
    if (!snapRes.hasSnapshot || !snapRes.snapshot) return null;
    const accountNumber = resolveAccountNumber(snapRes.snapshot, null);
    const options = await rhSnapshotToOptionPositions(
      snapRes.snapshot,
      accountNumber,
      sfSnapshot,
      remainingDteFn,
    );
    return {
      options,
      stock: sfSnapshot?.stock ?? [],
      fetchedAt: Date.now(),
    };
  } catch {
    return null;
  }
}
